import React, { Fragment, useCallback, useId, useState } from 'react';
import PropTypes from 'prop-types';
import ButtonGroup from 'reactstrap/lib/ButtonGroup';
import Button from 'reactstrap/lib/Button';
import Form from 'reactstrap/lib/Form';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { get, noop } from 'lodash';
import { useConstructor } from 'site-modules/shared/hooks/use-constructor';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { isTruckVehicle } from 'site-modules/shared/utils/is-truck-vehicle';
import { FieldError } from 'client/site-modules/shared/components/field-error/field-error';
import { FindStyleDrawer } from 'site-modules/shared/components/find-style-drawer/find-style-drawer';
import { ZipInput } from 'site-modules/shared/components/zip-input/zip-input';
import { FIND_STYLE_DRAWER_CREATIVE_ID } from 'site-modules/shared/constants/appraisal/appraisal';

import { MakeDropdown, StyledMakeDropdown } from './make-dropdown';
import { StyledSubmodelDropdown, SubmodelDropdown } from './submodel-dropdown';
import { ModelDropdown, StyledModelDropdown } from './model-dropdown';
import { StyledYearDropdown, YearDropdown } from './year-dropdown';
import {
  StyleDropdown,
  StyleDropdownWithSortedStyleId,
  StyledStyleDropdown,
  StyledStyleDropdownWithSortedStyleId,
} from './style-dropdown';
import { PubStateDropdown, StyledPubStateDropdown } from './pubstate-dropdown';
import { GoButton } from './go-button';
import { DROPDOWN_NAMES } from './constants';

import './vehicle-dropdowns.scss';

const DEFAULT_PUB_STATES = ['NEW', 'NEW_USED', 'USED'];
const DROPDOWN_CLASS_NAMES = {
  toggleClassName: 'bg-white medium',
  optionClassName: 'bg-white medium text-capitalize',
};

/**
 * Component that allows selection of a vehicle using Make/Model/Submodel/Year/Style dropdowns.
 * Whenever the last dropdown is selected the generateLink callback will be created href
 * for a link with the current selections.
 * Also we can choose which dropdowns should be displayed, across hasMake, hasModel, hasSubmodel, hasYear
 * and hasStyle properties,
 * we can pass them for displaying sposorshipLink.
 * iOS devices by default truncate large option text, if you don't need truncation pass hasOptionTruncate=false to
 * the component.
 */
function VehicleDropdownsUI({
  children,
  tag,
  pubStates,
  pubStatesYears,
  hasMake,
  hasModel,
  hasSubmodel,
  showAllSubmodels,
  hasYear,
  hasStyle,
  hasPubstate,
  hasOptionTruncate,
  hasZipInput,
  buttonClassNames,
  buttonColor,
  buttonTextColor,
  additionalYearOptions,
  makeLabel,
  modelLabel,
  yearLabel,
  styleLabel,
  zipLabel,
  submodelLabel,
  showLatestYears,
  splitNewUsedYears,
  labelClassName,
  inputClasses,
  makeDropdownClassNames,
  modelDropdownClassNames,
  submodelDropdownClassNames,
  yearDropdownClassNames,
  styleDropdownClassNames,
  pubstateDropdownClassNames,
  buttonGroupClassNames,
  isSmall,
  globalClasses,
  tracking,
  showArrowButton,
  showButtonGroup,
  hasSeparateLines,
  zipInputClasses,
  zipLabelClasses,
  zipWrapperClassNames,
  errorMake,
  errorModel,
  errorSubModel,
  errorYear,
  errorStyle,
  styleIds,
  isStyledSelects,
  showFindStyleLink,
  isMobile,
  updateStepsState,
  disabled,
  enableSpinner,
  rearrangeModelYearFields,
  noAdsRefresh,
  errorMessageClassNames,
  styleDropdownId,
  displayTipForTruckType,
  vehicle,
  selectStyle,
  generateLink,
  onDropdownsSelect,
  required,
  hasLiveRegions,
  storage,
  params,
  isVinDecoderUsed,
}) {
  const initialState = useConstructor(() => {
    const { make, model, submodels, year: yearValue } = vehicle || {};
    const year = yearValue ? { year: yearValue.toString() } : undefined;
    // style is an object in which are collected make, model, year objects
    const style = (selectStyle && vehicle) || undefined;

    const initialValues = { make, model, year, style, submodel: submodels, pubstate: undefined };

    if (initialValues.make && initialValues.model && initialValues.year && initialValues.style) {
      generateLink(initialValues.make, initialValues.model, initialValues.year, initialValues.style);
      onDropdownsSelect(initialValues.make, initialValues.model, initialValues.year, initialValues.style);
    }

    return initialValues;
  });

  const [make, setMake] = useState(initialState.make);
  const [model, setModel] = useState(initialState.model);
  const [year, setYear] = useState(initialState.year);
  const [style, setStyle] = useState(initialState.style);
  const [submodel, setSubmodel] = useState(get(vehicle, 'submodels'));
  const [pubstate, setPubstate] = useState();
  const [href, setHref] = useState();
  const makeErrorId = `make-error-${useId()}`;
  const modelErrorId = `model-error-${useId()}`;
  const submodelErrorId = `submodel-error-${useId()}`;
  const yearErrorId = `year-error-${useId()}`;
  const styleErrorId = `style-error-${useId()}`;
  const onDropdownSelectCallback = onDropdownsSelect || noop;
  const {
    make: isMakeRequired,
    model: isModelRequired,
    year: isYearRequired,
    submodel: isSubmodelRequired,
    style: isStyleRequired,
  } = required;

  const onMakeChange = useCallback(
    newMake => {
      onDropdownSelectCallback(newMake);

      setMake(newMake);
      setModel(undefined);
      setSubmodel(undefined);
      setYear(undefined);
      setPubstate(undefined);
      setStyle(undefined);
      setHref(!hasModel && !hasYear && !hasStyle && !hasPubstate ? generateLink(newMake) : undefined);
    },
    [generateLink, hasModel, hasPubstate, hasStyle, hasYear, onDropdownSelectCallback]
  );

  const onModelChange = useCallback(
    newModel => {
      onDropdownSelectCallback(make, newModel);

      setModel(newModel);
      setYear(undefined);
      setPubstate(undefined);
      setStyle(undefined);
      setHref(!hasYear && !hasStyle && !hasPubstate ? generateLink(make, newModel) : undefined);
    },
    [generateLink, hasPubstate, hasStyle, hasYear, make, onDropdownSelectCallback]
  );

  const onSubmodelChange = useCallback(
    newSubmodel => {
      const { model: newModel = undefined } = newSubmodel;
      onDropdownSelectCallback(make, newSubmodel);

      setModel(newModel);
      setSubmodel(newSubmodel);
      setYear(undefined);
      setPubstate(undefined);
      setHref(!hasYear && !hasStyle && !hasPubstate ? generateLink(make, newSubmodel) : undefined);
    },
    [generateLink, hasPubstate, hasStyle, hasYear, make, onDropdownSelectCallback]
  );

  const onYearChange = useCallback(
    newYear => {
      onDropdownSelectCallback(make, model, newYear);

      setYear(newYear);
      setStyle(undefined);
      setPubstate(undefined);
      setHref(!hasStyle && !hasPubstate ? generateLink(make, model, submodel, newYear) : undefined);
    },
    [generateLink, hasPubstate, hasStyle, make, model, onDropdownSelectCallback, submodel]
  );

  const onStyleChange = useCallback(
    newStyle => {
      const pubStateStyle = {
        ...newStyle,
        publicationState: get(year, 'displayPubState') || newStyle.publicationState,
      };
      onDropdownSelectCallback(make, model, year, pubStateStyle);

      setStyle(pubStateStyle);
      setHref(generateLink(make, model, year, pubStateStyle));
    },
    [generateLink, make, model, onDropdownSelectCallback, year]
  );

  const onPubStateChange = useCallback(
    newPubstate => {
      setPubstate(newPubstate);
      setHref(generateLink(make, model, submodel, newPubstate));
    },
    [generateLink, make, model, submodel]
  );

  const dropdownClasses = { ...DROPDOWN_CLASS_NAMES, inputClassName: inputClasses };

  let MakeDropdownComponent = MakeDropdown;
  let ModelDropdownComponent = ModelDropdown;
  let YearDropdownComponent = YearDropdown;
  let SubmodelDropdownComponent = SubmodelDropdown;
  let StyleDropdownComponent = styleIds ? StyleDropdownWithSortedStyleId : StyleDropdown;
  let PubStateDropdownComponent = PubStateDropdown;

  if (isStyledSelects) {
    MakeDropdownComponent = StyledMakeDropdown;
    ModelDropdownComponent = StyledModelDropdown;
    YearDropdownComponent = StyledYearDropdown;
    SubmodelDropdownComponent = StyledSubmodelDropdown;
    StyleDropdownComponent = styleIds ? StyledStyleDropdownWithSortedStyleId : StyledStyleDropdown;
    PubStateDropdownComponent = StyledPubStateDropdown;
  }

  const modelField = hasMake && hasModel && (
    <div
      className={classNames('dropdown', modelDropdownClassNames, {
        'has-danger': !!errorModel,
      })}
    >
      <ModelDropdownComponent
        make={make}
        value={model && model.name}
        enableSpinner={enableSpinner && !!make}
        onChange={onModelChange}
        pubStates={pubStates}
        data-tracking-id={tracking.modelSelectTrackingId}
        labelText={modelLabel}
        labelClassName={labelClassName}
        isLabelHidden={false}
        hasOptionTruncate={hasOptionTruncate}
        disabled={disabled}
        data-no-refresh={noAdsRefresh}
        name={DROPDOWN_NAMES.MODEL}
        {...dropdownClasses}
        {...(errorModel ? { ariaDescribedBy: modelErrorId, ariaInvalid: true } : {})}
        {...(isModelRequired ? { 'aria-required': true, required: true } : {})}
      />
      <FieldError
        id={modelErrorId}
        error={errorModel}
        classes={classNames(errorMessageClassNames)}
        isLiveRegion={hasLiveRegions}
      />
    </div>
  );
  const yearField = hasMake && (hasModel || hasSubmodel) && hasYear && (
    <div
      className={classNames('dropdown', yearDropdownClassNames, {
        'has-danger': !!errorYear,
      })}
    >
      <YearDropdownComponent
        make={make}
        model={model}
        enableSpinner={enableSpinner && !!make && !!model}
        submodel={submodel}
        value={year && (year.label || year.year)}
        onChange={onYearChange}
        pubStates={pubStatesYears || pubStates}
        data-tracking-id={tracking.yearSelectTrackingId}
        labelText={yearLabel}
        labelClassName={labelClassName}
        hasOptionTruncate={hasOptionTruncate}
        showLatestYears={showLatestYears}
        splitNewUsed={splitNewUsedYears}
        labelKey={splitNewUsedYears ? 'label' : undefined}
        valueKey={splitNewUsedYears ? 'label' : undefined}
        disabled={disabled}
        additionalOptions={additionalYearOptions}
        data-no-refresh={noAdsRefresh}
        name={DROPDOWN_NAMES.YEAR}
        {...dropdownClasses}
        {...(errorYear ? { ariaDescribedBy: yearErrorId, ariaInvalid: true } : {})}
        {...(isYearRequired ? { 'aria-required': true, required: true } : {})}
      />
      <FieldError
        id={yearErrorId}
        error={errorYear}
        classes={classNames(errorMessageClassNames)}
        isLiveRegion={hasLiveRegions}
      />
    </div>
  );

  const hasTipForTruckType = displayTipForTruckType && isTruckVehicle(vehicle);

  return (
    /**
     * Be sure to not cause <form> nesting and users should adjust `tag` prop if necessary to do so. See EVAL-2245
     */
    <Form
      className={classNames(
        'vehicle-dropdowns d-flex align-items-start text-start',
        hasMake + hasModel + hasSubmodel + hasStyle + hasYear < 4 && !isSmall && !hasSeparateLines
          ? 'flex-nowrap'
          : 'flex-wrap',
        globalClasses
      )}
      tag={tag}
      autoComplete={tag === 'form' ? 'off' : undefined}
    >
      {hasMake && (
        <div
          className={classNames('dropdown', makeDropdownClassNames, {
            'has-danger': !!errorMake,
          })}
        >
          <MakeDropdownComponent
            value={make && make.slug}
            onChange={onMakeChange}
            pubStates={pubStates}
            data-tracking-id={tracking.makeSelectTrackingId}
            labelText={makeLabel}
            labelClassName={labelClassName}
            isLabelHidden={false}
            hasOptionTruncate={hasOptionTruncate}
            disabled={disabled}
            data-no-refresh={noAdsRefresh}
            name={DROPDOWN_NAMES.MAKE}
            {...dropdownClasses}
            {...(errorMake ? { ariaDescribedBy: makeErrorId, ariaInvalid: true } : {})}
            {...(isMakeRequired ? { 'aria-required': true, required: true } : {})}
          />
          <FieldError
            id={makeErrorId}
            error={errorMake}
            classes={classNames(errorMessageClassNames)}
            isLiveRegion={hasLiveRegions}
          />
        </div>
      )}
      {rearrangeModelYearFields ? (
        <div className="d-flex justify-content-between w-100">
          {modelField}
          <div className="mx-0_5" />
          {yearField}
        </div>
      ) : (
        <Fragment>
          {modelField}
          {hasMake && hasSubmodel && (
            <div
              className={classNames('dropdown', submodelDropdownClassNames, {
                'has-danger': !!errorSubModel,
              })}
            >
              <SubmodelDropdownComponent
                make={make}
                value={submodel && submodel.name}
                onChange={onSubmodelChange}
                pubStates={pubStates}
                data-tracking-id={tracking.submodelSelectTrackingId}
                labelText={submodelLabel}
                labelClassName={labelClassName}
                hasOptionTruncate={hasOptionTruncate}
                showLatestYears={showLatestYears}
                disabled={disabled}
                data-no-refresh={noAdsRefresh}
                name={DROPDOWN_NAMES.SUBMODEL}
                {...dropdownClasses}
                {...(errorSubModel ? { ariaDescribedBy: submodelErrorId, ariaInvalid: true } : {})}
                {...(isSubmodelRequired ? { 'aria-required': true, required: true } : {})}
              />
              <FieldError id={submodelErrorId} error={errorSubModel} classes="mt-1" isLiveRegion={hasLiveRegions} />
            </div>
          )}
          {yearField}
        </Fragment>
      )}

      {hasMake && (hasModel || hasSubmodel) && hasYear && hasStyle && (
        <div className={classNames('dropdown', styleDropdownClassNames, { 'has-danger': !!errorStyle })}>
          <StyleDropdownComponent
            id={styleDropdownId}
            make={make}
            model={model}
            enableSpinner={enableSpinner && !!make && !!model && !!year}
            submodel={showAllSubmodels ? null : submodel}
            year={year}
            value={style && style.slug}
            styleIds={styleIds}
            onChange={onStyleChange}
            pubStates={pubStates}
            data-tracking-id={tracking.styleSelectTrackingId}
            data-tracking-value={
              tracking.styleSelectTrackingValuePath ? get(style, tracking.styleSelectTrackingValuePath) : undefined
            }
            labelText={styleLabel}
            labelClassName={labelClassName}
            hasOptionTruncate={hasOptionTruncate}
            disabled={disabled}
            data-no-refresh={noAdsRefresh}
            name={DROPDOWN_NAMES.STYLE}
            {...dropdownClasses}
            {...(errorStyle ? { ariaDescribedBy: styleErrorId, ariaInvalid: true } : {})}
            {...(isStyleRequired ? { 'aria-required': true, required: true } : {})}
          />
          <FieldError
            id={styleErrorId}
            error={errorStyle}
            classes={classNames({ 'style-error-desktop': !isMobile }, errorMessageClassNames)}
            isLiveRegion={hasLiveRegions}
          />
          {(hasTipForTruckType || showFindStyleLink) && (
            <div className={classNames('style-additions', { 'mt-md-2': !!errorStyle && !isMobile })}>
              {hasTipForTruckType && (
                <div className="style-engine-tip size-14 text-gray-dark mt-0_5" data-tracking-parent="style_engine_tip">
                  Note: Some engine types may be found in Step 2
                </div>
              )}
              {showFindStyleLink && (
                <FindStyleDrawer
                  creativeId={FIND_STYLE_DRAWER_CREATIVE_ID}
                  vehicle={{ make, model, year }}
                  onChangeStyle={onStyleChange}
                  selectedStyleValue={style?.slug}
                  isMobile={isMobile}
                  updateStepsState={updateStepsState}
                  style={style}
                  className="size-16 mt-0_5"
                  storage={storage}
                  params={params}
                  isVinDecoderUsed={isVinDecoderUsed}
                />
              )}
            </div>
          )}
        </div>
      )}
      {hasMake && hasSubmodel && hasPubstate && (
        <div className={classNames('dropdown dropdown-pubstate', pubstateDropdownClassNames)}>
          <PubStateDropdownComponent
            make={make}
            model={model}
            submodel={submodel}
            value={pubstate && pubstate.pubstate}
            onChange={onPubStateChange}
            data-tracking-id={tracking.pubstateSelectTrackingId}
            hasOptionTruncate={hasOptionTruncate}
            {...dropdownClasses}
          />
        </div>
      )}
      {hasZipInput && (
        <ZipInput
          wrapperClasses={zipWrapperClassNames}
          label={zipLabel}
          onValidate={validation.validateZip}
          inputClasses={zipInputClasses}
          labelClasses={zipLabelClasses}
          getTracking={tracking.zipInputTracking}
        />
      )}
      {showButtonGroup && (
        <ButtonGroup role="group" data-tracking-id={tracking.submitTrackingId} className={buttonGroupClassNames}>
          <GoButton
            isDisabled={!href}
            href={href}
            classes={buttonClassNames}
            buttonColor={buttonColor}
            textColor={buttonTextColor}
          />

          {showArrowButton && (
            <Button
              tag="span"
              color="success"
              className="d-inline-block active px-0 py-0_5 mb-1 js-go-btn icon-arrow-right3 ms-0"
              disabled={!href}
            />
          )}
        </ButtonGroup>
      )}
      {children && children({ make, model, submodel, year, style, pubstate })}
    </Form>
  );
}

VehicleDropdownsUI.propTypes = {
  children: PropTypes.func,
  tag: PropTypes.string,
  generateLink: PropTypes.func,
  pubStates: PropTypes.arrayOf(PropTypes.string),
  pubStatesYears: PropTypes.arrayOf(PropTypes.string),
  hasMake: PropTypes.bool,
  hasModel: PropTypes.bool,
  hasSubmodel: PropTypes.bool,
  showAllSubmodels: PropTypes.bool,
  hasYear: PropTypes.bool,
  hasStyle: PropTypes.bool,
  hasPubstate: PropTypes.bool,
  hasOptionTruncate: PropTypes.bool,
  hasZipInput: PropTypes.bool,
  selectStyle: PropTypes.bool,
  vehicle: PropTypes.shape({}),
  isSmall: PropTypes.bool,
  globalClasses: PropTypes.string,
  tracking: PropTypes.shape({
    submitTrackingId: PropTypes.string,
    makeSelectTrackingId: PropTypes.string,
    modelSelectTrackingId: PropTypes.string,
    yearSelectTrackingId: PropTypes.string,
    pubstateSelectTrackingId: PropTypes.string,
    submodelSelectTrackingId: PropTypes.string,
    styleSelectTrackingId: PropTypes.string,
    styleSelectTrackingValuePath: PropTypes.string,
  }),
  additionalYearOptions: PropTypes.arrayOf(PropTypes.shape({})),
  makeLabel: PropTypes.string,
  modelLabel: PropTypes.string,
  yearLabel: PropTypes.string,
  styleLabel: PropTypes.string,
  submodelLabel: PropTypes.string,
  zipLabel: PropTypes.string,
  inputClasses: PropTypes.string,
  errorMessageClassNames: PropTypes.string,
  labelClassName: PropTypes.string,
  makeDropdownClassNames: PropTypes.string,
  modelDropdownClassNames: PropTypes.string,
  submodelDropdownClassNames: PropTypes.string,
  yearDropdownClassNames: PropTypes.string,
  styleDropdownClassNames: PropTypes.string,
  zipWrapperClassNames: PropTypes.string,
  pubstateDropdownClassNames: PropTypes.string,
  buttonGroupClassNames: PropTypes.string,
  buttonClassNames: PropTypes.string,
  buttonColor: PropTypes.string,
  buttonTextColor: PropTypes.string,
  showArrowButton: PropTypes.bool,
  showButtonGroup: PropTypes.bool,
  showLatestYears: PropTypes.bool,
  splitNewUsedYears: PropTypes.bool,
  hasSeparateLines: PropTypes.bool,
  zipInputClasses: PropTypes.string,
  zipLabelClasses: PropTypes.string,
  onDropdownsSelect: PropTypes.func,
  errorMake: PropTypes.string,
  errorModel: PropTypes.string,
  errorSubModel: PropTypes.string,
  errorYear: PropTypes.string,
  errorStyle: PropTypes.string,
  isStyledSelects: PropTypes.bool,
  isStyleIdQueryParamEnabled: PropTypes.bool,
  showFindStyleLink: PropTypes.bool,
  isMobile: PropTypes.bool,
  updateStepsState: PropTypes.func,
  disabled: PropTypes.bool,
  enableSpinner: PropTypes.bool,
  rearrangeModelYearFields: PropTypes.bool,
  noAdsRefresh: PropTypes.bool,
  styleDropdownId: PropTypes.string,
  displayTipForTruckType: PropTypes.bool,
  required: PropTypes.shape({
    make: PropTypes.bool,
    model: PropTypes.bool,
    year: PropTypes.bool,
    style: PropTypes.bool,
    submodel: PropTypes.bool,
  }),
  hasLiveRegions: PropTypes.bool,
  storage: PropTypes.shape({}),
  params: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.string,
  }),
  isVinDecoderUsed: PropTypes.bool,
};

VehicleDropdownsUI.defaultProps = {
  children: null,
  tag: 'form',
  generateLink: noop,
  pubStates: DEFAULT_PUB_STATES,
  pubStatesYears: null,
  hasMake: true,
  hasModel: false,
  hasSubmodel: false,
  showAllSubmodels: false,
  hasYear: false,
  hasStyle: false,
  hasPubstate: false,
  hasZipInput: false,
  vehicle: null,
  isSmall: false,
  hasOptionTruncate: true,
  globalClasses: '',
  selectStyle: false,
  tracking: {},
  additionalYearOptions: null,
  makeLabel: '',
  modelLabel: '',
  yearLabel: '',
  styleLabel: '',
  zipLabel: 'ZIP Code',
  submodelLabel: '',
  inputClasses: '',
  errorMessageClassNames: 'mt-1',
  labelClassName: '',
  makeDropdownClassNames: '',
  modelDropdownClassNames: '',
  submodelDropdownClassNames: '',
  yearDropdownClassNames: '',
  styleDropdownClassNames: '',
  zipWrapperClassNames: '',
  pubstateDropdownClassNames: '',
  buttonGroupClassNames: 'mx-auto',
  buttonClassNames: 'px-1 py-0_5 mb-1',
  buttonColor: 'success',
  buttonTextColor: 'text-white',
  showArrowButton: true,
  showButtonGroup: true,
  showLatestYears: false,
  splitNewUsedYears: false,
  hasSeparateLines: false,
  zipInputClasses: '',
  zipLabelClasses: '',
  onDropdownsSelect: noop,
  errorMake: '',
  errorModel: '',
  errorSubModel: '',
  errorYear: '',
  errorStyle: '',
  isStyledSelects: false,
  isStyleIdQueryParamEnabled: false,
  showFindStyleLink: false,
  isMobile: false,
  enableSpinner: false,
  updateStepsState: noop,
  disabled: undefined,
  rearrangeModelYearFields: false,
  noAdsRefresh: false,
  styleDropdownId: '',
  displayTipForTruckType: false,
  required: {},
  hasLiveRegions: false,
  storage: null,
  params: {},
  isVinDecoderUsed: false,
};

const mapStateToProps = state => ({
  isSmall: get(state, 'pageContext.isSmallDeprecated'),
});

export const VehicleDropdowns = connect(mapStateToProps)(VehicleDropdownsUI);
